import React from 'react';

const CHUNK_RELOAD_KEY = 'sagax_chunk_reload_once';

const getChunkReloadGuardKey = () => {
  if (typeof window === 'undefined') return CHUNK_RELOAD_KEY;
  return `${CHUNK_RELOAD_KEY}:${window.location.pathname}`;
};

const clearChunkReloadGuard = () => {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(getChunkReloadGuardKey());
  } catch {
    // ignore
  }
};

const isChunkLoadError = (error) => {
  const message = `${error?.name || ''} ${error?.message || ''}`.toLowerCase();
  return (
    message.includes('chunkloaderror') ||
    message.includes('loading chunk') ||
    message.includes('failed to fetch dynamically imported module') ||
    message.includes('importing a module script failed')
  );
};

const markChunkReloadAttempt = () => {
  if (typeof window === 'undefined') return true;
  try {
    const key = getChunkReloadGuardKey();
    if (sessionStorage.getItem(key)) {
      return false;
    }
    sessionStorage.setItem(key, String(Date.now()));
    return true;
  } catch {
    return true;
  }
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, isAutoReloading: false };
    this.clearReloadGuardTimer = null;
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidMount() {
    this.clearReloadGuardTimer = window.setTimeout(() => {
      clearChunkReloadGuard();
      this.clearReloadGuardTimer = null;
    }, 15000);
  }

  componentWillUnmount() {
    if (this.clearReloadGuardTimer) {
      window.clearTimeout(this.clearReloadGuardTimer);
      this.clearReloadGuardTimer = null;
    }
  }

  componentDidCatch(error, errorInfo) {
    console.error('[ErrorBoundary] Caught error:', error, errorInfo);

    if (isChunkLoadError(error) && markChunkReloadAttempt()) {
      this.setState({ isAutoReloading: true });
      window.location.reload();
    }
  }

  handleRetry = () => {
    clearChunkReloadGuard();
    window.location.reload();
  };

  handleGoHome = () => {
    clearChunkReloadGuard();
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      const chunkError = isChunkLoadError(this.state.error);
      const message = this.state.isAutoReloading
        ? '检测到前端资源已更新，正在刷新到最新版本...'
        : chunkError
          ? '检测到页面资源版本不一致，请刷新到最新版本后重试。'
          : '页面渲染时发生错误，请重试或返回首页';

      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
          <div className="text-center max-w-md px-6">
            <div className="text-6xl mb-4">⚠️</div>
            <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-2">
              页面加载出错
            </h1>
            <p className="text-gray-500 dark:text-gray-400 mb-6">
              {message}
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={this.handleRetry}
                className="px-5 py-2.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium"
              >
                刷新页面
              </button>
              <button
                onClick={this.handleGoHome}
                className="px-5 py-2.5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors font-medium"
              >
                返回首页
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
